import React from 'react';
import { Scale } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
} from '@/components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  KpiCard, ChartCard, ExportMenu, ScreenHeader, EmptyMeter, UnpricedBadge, AXIS_TICK,
} from '@/components/app/kit.jsx';
import { useOps } from '@/hooks/useOps.jsx';
import { effectivePrice } from '@/lib/ops-data.js';
import { totalINR, groupSum, ranked, dailyTotals, dailyQuantity } from '@/lib/aggregate.js';
import {
  fmtINR, fmtMoney, fmtCompact, fmtNum, METRIC_LABEL, categoryOfProvider, todayIso,
} from '@/lib/format.js';
import { downloadCsv, downloadTxtReport } from '@/lib/export.js';
import { LoadingBody, ErrorBody } from './Overview.jsx';

// By provider — daily quantity + cost, unit volumes × current rate, and the
// reconciliation chip: metered-cost (our hooks × prices) vs provider-billed
// (source=provider-api / aws billed-cost rows), absolute + % gap.
export default function ByProvider({ range }) {
  const ops = useOps();
  const [provider, setProvider] = React.useState('');

  if (ops.loading) return <LoadingBody />;
  if (ops.error) return <ErrorBody error={ops.error} />;
  const items = ops.enriched;
  if (!items.length) return <div className="p-6"><ScreenHeader title="By provider" /><EmptyMeter /></div>;

  const providers = ranked(groupSum(items, (r) => r.provider)).map((r) => r.key);
  const active = provider && providers.includes(provider) ? provider : providers[0];

  const mine = items.filter((r) => r.provider === active);
  const metered = mine.filter((r) => r.source === 'metered');
  const billedRows = mine.filter((r) => r.metric === 'billed-cost');
  const meteredTotal = totalINR(metered);
  const billedTotal = totalINR(billedRows);
  const grand = totalINR(mine);

  // Reconciliation: only meaningful when both sides exist.
  const gap = billedTotal.inr > 0 ? meteredTotal.inr - billedTotal.inr : null;
  const gapPct = gap !== null && billedTotal.inr > 0 ? (gap / billedTotal.inr) * 100 : null;

  // Daily cost (INR) + daily quantity (largest metered metric drives the qty axis).
  const costSeries = dailyTotals(mine, range);
  const metricGroups = ranked(groupSum(metered, (r) => r.metric));
  const primaryMetric = metricGroups.length ? metricGroups[0].key : null;
  const qtySeries = primaryMetric
    ? dailyQuantity(metered.filter((r) => r.metric === primaryMetric), range)
    : [];
  const rows = costSeries.map((c, i) => ({
    label: c.label, cost: Math.round(c.value * 100) / 100, qty: qtySeries[i] ? qtySeries[i].value : 0,
  }));

  // Unit volumes × current rate table (one row per product × metric).
  const today = todayIso();
  const volMap = groupSum(metered, (r) => `${r.product}|${r.metric}`);
  const volRows = [...volMap.entries()].map(([k, g]) => {
    const [product, metric] = k.split('|');
    const priceKey = `${active}:${product}:${metric}`;
    const price = effectivePrice(ops.byKey, priceKey, today);
    const qty = metered.filter((r) => r.product === product && r.metric === metric)
      .reduce((s, r) => s + r.value, 0);
    return {
      product, metric, qty,
      rate: price ? `${fmtMoney(price.amount, price.currency)} ${price.basis}` : null,
      inr: g.inr, unpricedCount: g.unpricedCount, unpricedQty: g.unpricedQty,
    };
  }).sort((a, b) => b.inr - a.inr);

  const csv = () => downloadCsv(
    `ops-provider-${active}-${range.from}_${range.to}.csv`,
    [
      { key: 'product', label: 'Product' }, { key: 'metric', label: 'Metric' },
      { key: 'qty', label: 'Quantity' }, { key: 'rate', label: 'Current rate' },
      { key: 'inr', label: 'Cost (INR)' },
    ],
    volRows.map((r) => ({ ...r, inr: r.inr.toFixed(2), rate: r.rate || 'UNPRICED' })),
  );
  const txt = () => downloadTxtReport(`ops-provider-${active}-${range.from}_${range.to}.txt`, {
    title: `Healther Ops — Provider: ${active}`,
    meta: [
      `Range: ${range.from} → ${range.to}`,
      `Total (INR): ${fmtINR(grand.inr)}`,
      `Metered (INR): ${fmtINR(meteredTotal.inr)}`,
      billedTotal.inr > 0 ? `Provider-billed (INR): ${fmtINR(billedTotal.inr)}` : 'Provider-billed: none reported',
      gap !== null ? `Reconciliation gap: ${fmtINR(gap)} (${gapPct.toFixed(1)}%)` : 'Reconciliation: n/a',
    ],
    sections: [{
      heading: 'Unit volumes × current rate',
      columns: [
        { key: 'product', label: 'Product' }, { key: 'metric', label: 'Metric' },
        { key: 'qty', label: 'Qty' }, { key: 'rate', label: 'Rate' }, { key: 'inr', label: 'INR' },
      ],
      rows: volRows.map((r) => ({ ...r, qty: Math.round(r.qty), inr: Math.round(r.inr), rate: r.rate || 'UNPRICED' })),
    }],
  });

  return (
    <div className="p-6 cc-enter">
      <ScreenHeader title="By provider" subtitle={`Spend, volumes and reconciliation · ${categoryOfProvider(active)}`}>
        <Select value={active} onValueChange={setProvider}>
          <SelectTrigger size="sm" className="min-w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            {providers.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <ExportMenu onCsv={csv} onTxt={txt} />
      </ScreenHeader>

      <div className="mb-4 flex flex-wrap gap-3">
        <KpiCard label="Total spend" value={fmtINR(grand.inr)} sub="metered + billed, INR" />
        <KpiCard label="Metered cost" value={fmtINR(meteredTotal.inr)}
          sub={`${metered.length} rollups × prices`} />
        <KpiCard label="Provider-billed" value={billedTotal.inr > 0 ? fmtINR(billedTotal.inr) : '—'}
          sub={billedTotal.inr > 0 ? 'from usage/billing API' : 'not reported for this provider'} />
        <ReconCard gap={gap} gapPct={gapPct} />
      </div>

      <ChartCard
        title="Daily cost & volume"
        subtitle={primaryMetric ? `INR cost (bars) vs ${METRIC_LABEL[primaryMetric] || primaryMetric} (line)` : 'INR cost per day'}
        empty={rows.every((r) => !r.cost && !r.qty)}
      >
        <ChartContainer
          config={{
            cost: { label: 'Cost (INR)', color: 'hsl(var(--chart-1))' },
            qty: { label: primaryMetric ? (METRIC_LABEL[primaryMetric] || primaryMetric) : 'Quantity', color: 'hsl(var(--chart-3))' },
          }}
          className="h-[220px] w-full"
        >
          <ComposedChart data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} tick={AXIS_TICK} minTickGap={24} />
            <YAxis yAxisId="cost" width={52} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={(v) => fmtCompact(v)} />
            <YAxis yAxisId="qty" orientation="right" width={48} tickLine={false} axisLine={false} tick={AXIS_TICK} tickFormatter={(v) => fmtCompact(v)} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            <Bar yAxisId="cost" dataKey="cost" fill="var(--color-cost)" radius={[3, 3, 0, 0]} />
            <Line yAxisId="qty" dataKey="qty" type="monotone" stroke="var(--color-qty)" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ChartContainer>
      </ChartCard>

      <Card className="mt-4 gap-0 overflow-hidden py-0">
        <CardHeader className="border-b py-4">
          <CardTitle className="text-[13.5px]">Unit volumes × current rate</CardTitle>
          <CardDescription className="text-[11.5px]">Metered usage in this range, priced at today's effective rate</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {volRows.length === 0 ? (
            <div className="px-4 py-6 text-[12.5px] text-muted-foreground">
              No metered usage — this provider only reports billed totals.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-4">Product</TableHead>
                    <TableHead>Metric</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Current rate</TableHead>
                    <TableHead className="pr-4 text-right">Cost (INR)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {volRows.map((r) => (
                    <TableRow key={r.product + r.metric}>
                      <TableCell className="pl-4 font-medium text-foreground">{r.product}</TableCell>
                      <TableCell className="text-muted-foreground">{METRIC_LABEL[r.metric] || r.metric}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtNum(r.qty)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {r.rate || <UnpricedBadge qty={r.unpricedQty} count={r.unpricedCount} />}
                      </TableCell>
                      <TableCell className="pr-4 text-right tabular-nums font-medium text-foreground">
                        {r.unpricedCount && !r.inr ? '—' : fmtINR(r.inr)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// The reconciliation chip: metered vs provider-billed with absolute + % gap.
function ReconCard({ gap, gapPct }) {
  let value = '—';
  let sub = 'needs both metered & billed';
  let tone;
  if (gap !== null) {
    value = `${gap >= 0 ? '+' : '−'}${fmtINR(Math.abs(gap))}`;
    sub = `${gapPct >= 0 ? '+' : ''}${gapPct.toFixed(1)}% vs provider bill`;
    tone = Math.abs(gapPct) > 15 ? 'text-[hsl(var(--allergy))]'
      : Math.abs(gapPct) > 5 ? 'text-[hsl(var(--ai-foreground))]'
        : 'text-[hsl(var(--success))]';
  }
  return (
    <Card className="min-w-0 flex-1 gap-2 py-4">
      <CardContent className="flex flex-col gap-1.5 px-4">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Scale className="size-3.5" /> Reconciliation
        </div>
        <div className={`text-[23px] font-semibold leading-none tabular-nums ${tone || 'text-foreground'}`}>{value}</div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">{sub}</span>
          {gap !== null && Math.abs(gapPct) <= 5 && (
            <Badge variant="outline" className="h-4 border-[hsl(var(--success))]/40 px-1.5 text-[9.5px] font-semibold text-[hsl(var(--success))]">IN LINE</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
